import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, Button } from "reactstrap";

import {
  dismissQuickstart,
  getStatus,
  QuickstartStatus,
} from "../../../services/onboardingService";

/**
 * Thin day-0 hint (replaces giant Quickstart card). One next step only.
 */
const ActivationHintBanner = () => {
  const { t } = useTranslation();
  const [quickstart, setQuickstart] = useState<QuickstartStatus | null>(null);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await getStatus();
      setQuickstart(response.data.quickstart);
    } catch {
      setQuickstart(null);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!quickstart?.visible || quickstart.salon_live) {
    return null;
  }

  const nextStep = quickstart.steps.find((step) => !step.done);
  if (!nextStep) {
    return null;
  }

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      const response = await dismissQuickstart();
      setQuickstart(response.data.quickstart);
    } catch {
      // Keep banner; user can retry dismiss.
    } finally {
      setDismissing(false);
    }
  };

  return (
    <Alert
      color="success"
      className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 py-2"
    >
      <div className="d-flex align-items-start gap-2">
        <i className="ri-flashlight-line fs-4 mt-1" aria-hidden="true" />
        <div>
          <strong className="d-block">
            {t("studio.dashboard.activationHint.title")}
          </strong>
          <span className="small">
            {t(`onboarding.quickstart.steps.${nextStep.key}`)}
          </span>
        </div>
      </div>
      <div className="d-flex flex-wrap gap-2">
        <Button color="link" size="sm" className="text-muted px-2" type="button" onClick={() => void handleDismiss()} disabled={dismissing}>
          {t("studio.dashboard.activationHint.dismiss")}
        </Button>
        <Button tag={Link} to={nextStep.cta} color="success" size="sm">
          {t("studio.dashboard.activationHint.cta")}
        </Button>
      </div>
    </Alert>
  );
};

export default ActivationHintBanner;
